/**
 * DV-TEMP-004: Temporal Web Link Utilities
 *
 * Utilities for generating links to Temporal Web UI for debugging workflow executions.
 * Provides deep links to workflow runs, execution history, and task details.
 */

export interface TemporalWebConfig {
  baseUrl: string;
  namespace: string;
}

export interface WorkflowIdentifiers {
  workflowId?: string;
  runId?: string;
  namespace?: string;
}

/**
 * Default Temporal Web configuration
 * Uses environment variables with fallbacks for development
 */
export const DEFAULT_TEMPORAL_CONFIG: TemporalWebConfig = {
  baseUrl: import.meta.env.VITE_TEMPORAL_WEB_URL ||
           import.meta.env.VITE_TEMPORAL_UI_URL ||
           'https://temporal.hi-si.ai', // Production fallback
  namespace: import.meta.env.VITE_TEMPORAL_NAMESPACE || 'default'
};

/**
 * Extract workflow identifiers from execution ID or event data
 * Supports various formats of execution IDs that might contain workflow information
 */
export function extractWorkflowIdentifiers(
  executionId: string,
  workflowRunId?: string
): WorkflowIdentifiers {
  // For our temporal demo viewer, the execution ID IS the workflow ID
  // Use the full execution ID as the workflow ID for Temporal Web links
  const workflowId = executionId;

  return {
    workflowId,
    runId: workflowRunId,
    namespace: DEFAULT_TEMPORAL_CONFIG.namespace
  };
}

/**
 * Generate a link to the workflow execution in Temporal Web
 */
export function generateWorkflowLink(
  identifiers: WorkflowIdentifiers,
  config: TemporalWebConfig = DEFAULT_TEMPORAL_CONFIG
): string | null {
  const { workflowId, runId, namespace = config.namespace } = identifiers;

  if (!workflowId) {
    return null;
  }

  // Base workflow URL
  let url = `${config.baseUrl}/namespaces/${encodeURIComponent(namespace)}/workflows/${encodeURIComponent(workflowId)}`;

  // Add run ID if available for more specific link
  if (runId) {
    url += `/${encodeURIComponent(runId)}`;
  }

  return url;
}

/**
 * Generate a link to the workflow execution history in Temporal Web
 */
export function generateWorkflowHistoryLink(
  identifiers: WorkflowIdentifiers,
  config: TemporalWebConfig = DEFAULT_TEMPORAL_CONFIG
): string | null {
  const baseLink = generateWorkflowLink(identifiers, config);
  return baseLink ? `${baseLink}/history` : null;
}

/**
 * Generate a link to workflow execution summary
 */
export function generateWorkflowSummaryLink(
  identifiers: WorkflowIdentifiers,
  config: TemporalWebConfig = DEFAULT_TEMPORAL_CONFIG
): string | null {
  const baseLink = generateWorkflowLink(identifiers, config);
  return baseLink ? `${baseLink}/summary` : null;
}

/**
 * Generate a link to workflow stack trace (for failed workflows)
 */
export function generateWorkflowStackTraceLink(
  identifiers: WorkflowIdentifiers,
  config: TemporalWebConfig = DEFAULT_TEMPORAL_CONFIG
): string | null {
  const baseLink = generateWorkflowLink(identifiers, config);
  return baseLink ? `${baseLink}/stack-trace` : null;
}

/**
 * Check if workflow identifiers are sufficient for generating links
 */
export function canGenerateWorkflowLink(identifiers: WorkflowIdentifiers): boolean {
  return Boolean(identifiers.workflowId);
}

/**
 * Generate all available Temporal Web links for a given set of identifiers
 */
export function generateAllTemporalLinks(
  identifiers: WorkflowIdentifiers,
  config: TemporalWebConfig = DEFAULT_TEMPORAL_CONFIG
): {
  workflow?: string;
  history?: string;
  summary?: string;
  stackTrace?: string;
} {
  if (!canGenerateWorkflowLink(identifiers)) {
    return {};
  }

  return {
    workflow: generateWorkflowLink(identifiers, config) || undefined,
    history: generateWorkflowHistoryLink(identifiers, config) || undefined,
    summary: generateWorkflowSummaryLink(identifiers, config) || undefined,
    stackTrace: generateWorkflowStackTraceLink(identifiers, config) || undefined
  };
}

/**
 * Format workflow identifiers for display
 */
export function formatWorkflowIdentifiers(identifiers: WorkflowIdentifiers): {
  workflowId?: string;
  runId?: string;
  namespace?: string;
} {
  return {
    workflowId: identifiers.workflowId, // Show full workflow ID (execution ID)
    runId: identifiers.runId?.slice(-8), // Show last 8 chars of run ID
    namespace: identifiers.namespace
  };
}

/**
 * React hook for generating Temporal Web links
 */
export function useTemporalLinks(
  executionId: string,
  workflowRunId?: string,
  config?: TemporalWebConfig
) {
  const identifiers = extractWorkflowIdentifiers(executionId, workflowRunId);
  const links = generateAllTemporalLinks(identifiers, config);
  const formatted = formatWorkflowIdentifiers(identifiers);
  const canGenerate = canGenerateWorkflowLink(identifiers);

  return {
    identifiers,
    links,
    formatted,
    canGenerate
  };
}